import React, { Suspense, lazy, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Box, Loader2, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { PV_TR } from "@/lib/pipeVizI18n";
import { MaterialList } from "@/components/MaterialList";
import { CutLengths } from "@/components/CutLengths";

const Pipe3D = lazy(() => import("@/components/Pipe3D"));

export default function SharedIsometry() {
  const { id } = useParams();
  const { user } = useAuth();
  const { lang } = useLang();
  const t = PV_TR[lang] || PV_TR.sk;
  const [model, setModel] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/pipe-viz/shared/${id}`).then((r) => setModel(r.data)).catch(() => setMissing(true));
  }, [id]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="shared-isometry-page">
      <div className="font-mono text-[10px] tracking-[0.3em] text-amber-400 mb-3">{t.tag}</div>
      <h1 className="font-display font-black text-3xl sm:text-5xl text-white leading-none">
        {model?.owner_name ? t.sharedBy.replace("{name}", model.owner_name) : t.result}
      </h1>
      <div className="mt-6 border border-zinc-800 bg-zinc-950 aspect-[4/3] flex items-center justify-center overflow-hidden" data-testid="shared-isometry-preview">
        {missing ? <div className="text-zinc-500 text-sm" data-testid="shared-isometry-missing">{t.sharedMissing}</div>
          : model ? (
            <Suspense fallback={<Loader2 className="w-8 h-8 animate-spin text-amber-400" />}>
              <Pipe3D segments={model.segments} diameter={model.pipe_diameter} fittings={model.fittings} />
            </Suspense>
          ) : <Loader2 className="w-8 h-8 animate-spin text-amber-400" />}
      </div>
      {model && <div className="text-xs text-zinc-500 mt-3">{t.hint}</div>}
      {model && <div className="mt-4"><MaterialList segments={model.segments} fittings={model.fittings} diameter={model.pipe_diameter} lang={lang} /></div>}
      {model && <div className="mt-4"><CutLengths segments={model.segments} fittings={model.fittings} diameter={model.pipe_diameter} lang={lang} /></div>}
      <div className="mt-8 border border-amber-500/40 bg-amber-500/5 p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <div className="text-white font-display text-lg">{t.invite}</div>
        <Link to={user ? "/pipe-visualizer" : "/register"} data-testid="shared-isometry-cta"
              className="wb-btn-primary px-5 py-3 text-sm inline-flex items-center gap-2 justify-center">
          {user ? <Box className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />} {user ? t.generate : t.joinFree}
        </Link>
      </div>
    </div>
  );
}
